import {
    ClaimApi,
    CoreFetch,
    CreateClaimFacility,
    CreateClaimProvider,
    CreateClaimGuarantor,
    CreateClaimPatient,
    CreateClaimSubscriber,
    GuarantorApi,
    PatientApi,
    PayerApi,
    PayerSubscriberApi,
    SubscriberApi,
    ProfessionalApi,
    FacilityApi,
    DocumentApi,
    ProviderCreateDocumentType,
    SupportDocumentType,
    AddressType,
    // SupportClaimDetails,
    ProviderGuarantor,
    ProviderPayer,
    ProviderPayerSubscriber,
    ProviderSubscriber,
    ProviderPatient,
    ProviderApi,
    RelationshipType,
} from '../../api';
import _ from 'lodash';
import { action, makeObservable, observable, runInAction, computed } from 'mobx';
import { AdditionalInformation, IClaimEditorStoreConfig } from './claim-editor.types';
import { canSubmitClaim, getSubmitBlockers } from './claim-editor-validation';
import { CoreDocumentsStore } from '../documents.store';

export class ClaimEditorStore {
    @observable public facility: CreateClaimFacility | null = null;
    @observable public patient: CreateClaimPatient | null = null;
    @observable public guarantor: CreateClaimGuarantor | null = null;
    @observable public providers: CreateClaimProvider[] = [];
    @observable public additionalInformation: AdditionalInformation | null = null;
    @observable public services: any[] = [];
    @observable public subscribers: CreateClaimSubscriber[] = [];
    @observable public billingProviderId: string | null = null;
    @observable public isLoading = false;
    @observable public claimDetails: any | null = null;
    @observable public activePayload: any = null;
    @observable public settings: { maximumGuarantorResponsibility?: { amount: string; currency: string }; primaryThirdPartyAdministratorPayerId?: string; secondaryThirdPartyAdministratorPayerId?: string; tertiaryThirdPartyAdministratorPayerId?: string } | null = null;
    @observable public guarantorPayments: Array<{ collectionDate?: string; amount?: { amount: string; currency: string }; memo?: string }> = [];
    @observable public seller: { sellerId?: string; externalId?: string; first?: string; last?: string; organization?: string } | null = null;
    @observable public newDocument: File | null = null;
    @observable public documents: string[] = [];
    @observable public validationSignal: number = 0;
    @observable public isTemplate: boolean = false;
    @observable public isCreateClaim: boolean = true;
    @observable public isControlNumberUnique: boolean | null = null;
    @observable private originalControlNumber: string | null = null;

    private config?: IClaimEditorStoreConfig;
    private documentsStore: CoreDocumentsStore;
    @observable private billingProvidersCache: any[] = [];
    @observable private billingProvidersLoaded: boolean = false;
    @observable private payersCache: ProviderPayer[] = [];
    @observable private payersLoaded: boolean = false;
    private initialClaimId: string | null = null;
    private controlNumberCheckToken = 0;

    constructor(config?: IClaimEditorStoreConfig) {
        makeObservable(this);
        this.config = config;
        this.documentsStore = new CoreDocumentsStore({ supportGrant: (config as any)?.supportGrant });
    }

    private get isSupportApp(): boolean {
        return Boolean((this.config as any)?.supportGrant);
    }

    @action
    public setSupportGrant = (support: boolean | undefined): void => {
        this.config = { ...(this.config as any), supportGrant: Boolean(support) } as any;
        this.documentsStore.setSupportGrant(support);
    };
    @action
    public triggerValidation = (): void => {
        this.validationSignal = (this.validationSignal || 0) + 1;
    };
    @computed
    public get canSubmit(): boolean {
        return canSubmitClaim({
            patient: this.patient,
            guarantor: this.guarantor,
            billingProviderId: this.billingProviderId,
            services: this.services,
            providers: this.providers,
            additionalInformation: this.additionalInformation,
            guarantorPayments: this.guarantorPayments,
            seller: this.seller,
            facility: this.facility,
            subscribers: this.subscribers,
        });
    }

    public getSubmitBlockers(): string[] {
        return getSubmitBlockers({
            patient: this.patient,
            guarantor: this.guarantor,
            billingProviderId: this.billingProviderId,
            services: this.services,
            providers: this.providers,
            additionalInformation: this.additionalInformation,
            guarantorPayments: this.guarantorPayments,
            seller: this.seller,
            facility: this.facility,
            subscribers: this.subscribers,
        });
    }

    private deepClean = (input: any): any => {
        if (Array.isArray(input)) {
            const arr = input
                .map(this.deepClean)
                .filter((v) => v !== undefined && v !== null && !(typeof v === 'string' && v.trim() === ''))
                .filter((v) => !(Array.isArray(v) && v.length === 0))
                .filter((v) => !(typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0));
            return arr;
        }
        if (input && typeof input === 'object') {
            const out: any = {};
            Object.keys(input).forEach((k) => {
                const v = this.deepClean((input as any)[k]);
                const isEmptyString = typeof v === 'string' && v.trim() === '';
                const isNullish = v === undefined || v === null;
                const isEmptyArray = Array.isArray(v) && v.length === 0;
                const isEmptyObject = v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0;
                if (!isNullish && !isEmptyString && !isEmptyArray && !isEmptyObject) {
                    out[k] = v;
                }
            });
            return out;
        }
        return input;
    };


    @action
    public loadClaimData = async (claimId: string, isTemplate = false): Promise<void> => {
        this.isLoading = true;
        runInAction(() => {
            this.isTemplate = Boolean(isTemplate);
            this.initialClaimId = claimId || null;
            this.isCreateClaim = !this.initialClaimId || this.isTemplate;
            this.originalControlNumber = null;
        });

        try {
            // Step 1: Fetch claim details
            const details = await CoreFetch<ClaimApi>(ClaimApi)[
                this.isSupportApp ? 'getSupportClaimDetails' : 'getProviderClaimDetails'
            ]({
                id: claimId,
            } as any);

            runInAction(() => {
                this.claimDetails = details;
            });

            // Try to fetch professional claim payload (Support-only endpoint) by claimId first
            let activePayload: any = null;
            if (this.isSupportApp && claimId) {
                try {
                    const byClaimId = await CoreFetch<ProfessionalApi>(ProfessionalApi)
                        .getSupportProfessionalClaimClaimPayload({ id: claimId } as any);
                    if (byClaimId && Object.keys(byClaimId as any).length > 0) {
                        activePayload = byClaimId;
                    }
                } catch (e) {
                    // noop, fallback below
                }
            }

            // Fallback to HL7-derived payload if needed
            if (!activePayload) {
                activePayload = this.determineActivePayload(isTemplate);
            }

            // Capture additional information fields BEFORE omitting for templates
            const capturedAdditionalInfo = activePayload ? {
                controlNumber: activePayload.controlNumber || '',
                claimNarrative: activePayload.claimNarrative || '',
                admissionDate: activePayload.admissionDate || '',
                dischargeDate: activePayload.dischargeDate || '',
                primaryPayerAuthorizationNumber: activePayload.primaryPayerAuthorizationNumber || '',
                secondaryPayerAuthorizationNumber: activePayload.secondaryPayerAuthorizationNumber || '',
                tertiaryPayerAuthorizationNumber: activePayload.tertiaryPayerAuthorizationNumber || '',
                otherDateType: activePayload.otherDateType || '',
                fromOtherDate: activePayload.fromOtherDate || '',
                throughOtherDate: activePayload.throughOtherDate || '',
            } : null;

            if (isTemplate) {
                // Remove fields to create new claim from template
                activePayload = _.omit(activePayload, ['controlNumber', 'claimId']);
            }

            runInAction(() => {
                this.activePayload = activePayload;
                
                // Extract facility data from active payload
                if (activePayload?.facility) {
                    this.facility = activePayload.facility;
                }

                // Extract patient from active payload
                if (activePayload?.patient) {
                    this.patient = activePayload.patient as CreateClaimPatient;
                }

                // Extract guarantor from active payload
                if (activePayload?.guarantor) {
                    this.guarantor = activePayload.guarantor as CreateClaimGuarantor;
                }

                // Extract services / subscribers / billing provider
                this.services = Array.isArray(activePayload?.services) ? activePayload.services : [];
                this.subscribers = Array.isArray(activePayload?.subscribers) ? activePayload.subscribers : [];
                this.billingProviderId = (activePayload?.billingProviderId as any) || null;

                // Extract providers from active payload if present
                try {
                    const providersData: any[] = Array.isArray(activePayload?.providers) ? activePayload.providers : [];
                    if (providersData && providersData.length > 0) {
                        this.providers = providersData.map(p => ({
                            first: p.first || p.provider?.first || undefined,
                            middle: p.middle || p.provider?.middle || undefined,
                            last: p.last || p.provider?.last || undefined,
                            suffix: p.suffix || p.provider?.suffix || undefined,
                            npi: p.npi || p.provider?.npi || undefined,
                            providerId: p.providerId || p.provider?.id || undefined,
                            type: p.type,
                        } as CreateClaimProvider));
                    }
                } catch (e) {
                    // noop
                }

                if (activePayload) {
                    this.settings = {
                        maximumGuarantorResponsibility: activePayload.maximumGuarantorResponsibility || undefined,
                        primaryThirdPartyAdministratorPayerId: activePayload.primaryThirdPartyAdministratorPayerId || undefined,
                        secondaryThirdPartyAdministratorPayerId: activePayload.secondaryThirdPartyAdministratorPayerId || undefined,
                        tertiaryThirdPartyAdministratorPayerId: activePayload.tertiaryThirdPartyAdministratorPayerId || undefined,
                    };
                }

                if (activePayload?.guarantorPayments && Array.isArray(activePayload.guarantorPayments)) {
                    this.guarantorPayments = activePayload.guarantorPayments;
                }

                if (activePayload?.seller) {
                    const sellerPayload: any = activePayload.seller;
                    this.seller = {
                        sellerId: sellerPayload?.sellerId,
                        ...sellerPayload,
                    };
                }

                // Extract additional information from captured values (before template omission)
                // For templates, controlNumber and claimId will be empty/undefined (as intended for new claims)
                if (capturedAdditionalInfo) {
                    this.additionalInformation = isTemplate
                        ? { ...capturedAdditionalInfo, controlNumber: '' }
                        : capturedAdditionalInfo;
                } else {
                    this.additionalInformation = null;
                }

                const normalizedOriginal = capturedAdditionalInfo?.controlNumber
                    ? String(capturedAdditionalInfo.controlNumber).trim()
                    : null;
                this.originalControlNumber = normalizedOriginal;
                this.isControlNumberUnique = normalizedOriginal && !this.isTemplate ? false : null;
                this.updateIsCreateClaimStatus(this.additionalInformation?.controlNumber);
            });
        } catch (error) {
            console.error('Failed to load claim data:', error);
            
            // Call app-specific error handler if provided
            if (this.config?.onLoadError && error instanceof Error) {
                this.config.onLoadError(error);
            }
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    private determineActivePayload(isTemplate: boolean): any {
        // Use payload from lastRelevantHl7
        console.log('Using HL7 payload');
        
        const sortedHl7s = this.claimDetails?.hl7s
            ?.sort((a: any, b: any) => new Date(b.created || 0).getTime() - new Date(a.created || 0).getTime());

        const lastRelevant = sortedHl7s?.find(
            (hl7: any) => hl7?.providerProfessional || hl7?.supportProfessional || hl7?.professional,
        );

        const result = lastRelevant?.providerProfessional ?? lastRelevant?.supportProfessional ?? lastRelevant?.professional;

        // Fallback: if no professional payload found, return an empty payload to behave like create-claim
        const fallback: any = { services: [], subscribers: [], providers: [] };

        // Remove fields to create new claim from template
        if (isTemplate) {
            return _.omit(result || fallback, ['controlNumber', 'claimId']);
        }
        
        return result || fallback;
    }

    @action
    public updateFacility = (facilityData: CreateClaimFacility | null): void => {
        this.facility = facilityData;
    };

    @action
    public updatePatient = (patientData: CreateClaimPatient | null): void => {
        this.patient = patientData;
    };

    @action
    public updateGuarantor = (guarantorData: CreateClaimGuarantor | null): void => {
        this.guarantor = guarantorData;
    };

    @action
    public copyGuarantorFromPatient = (): void => {
        const patient = this.patient;
        if (!patient) return;

        const guarantor: CreateClaimGuarantor = {
            first: patient.first,
            middle: patient.middle,
            last: patient.last,
            dob: patient.dob,
            phone: patient.phones?.[0],
            email: patient.emails?.[0],
            addresses: patient.addresses,
        } as any;

        this.guarantor = guarantor;
    };

    @action
    public updateProviders = (providers: CreateClaimProvider[]): void => {
        this.providers = providers;
    };

    @action
    public deleteFacility = (): void => {
        this.facility = null;
    };

    @action
    public deletePatient = (): void => {
        this.patient = null;
    };

    @action
    public updateAdditionalInformation = (informationData: AdditionalInformation | null): void => {
        this.additionalInformation = informationData;
        this.updateIsCreateClaimStatus(informationData?.controlNumber);
    };

    @action
    public updateServices = (servicesData: any[] | null): void => {
        const incoming: any[] = Array.isArray(servicesData) ? servicesData : [];
        const previous: any[] = Array.isArray(this.services) ? this.services : [];

        // Preserve nested fields on existing service lines unless explicitly provided
        const merged = incoming.map((svc, index) => {
            const nextSvc: any = { ...(svc || {}) };
            const prevSvc: any = previous[index] || {}; 

            if (typeof nextSvc.providers === 'undefined' && typeof prevSvc.providers !== 'undefined') {
                nextSvc.providers = prevSvc.providers;
            }
            if (typeof nextSvc.facility === 'undefined' && typeof prevSvc.facility !== 'undefined') {
                nextSvc.facility = prevSvc.facility;
            }
            // Preserve drug object if not explicitly provided
            if (typeof nextSvc.drug === 'undefined' && typeof prevSvc.drug !== 'undefined') {
                nextSvc.drug = prevSvc.drug;
            }
            return nextSvc;
        }); 
        this.services = merged;
    };

    /**
     * Update one service line (partial merge) without clobbering nested fields.
     *
     * Use this for granular, debounced edits inside a service form. Only pass the flat
     * properties being edited; nested structures such as providers/facility will be
     * preserved unless explicitly provided.
     */
    @action
    public updateServiceLine = (serviceIndex: number, serviceData: any): void => {
        const next = [...(this.services || [])];
        const prevSvc: any = next[serviceIndex] || {};
        // Preserve nested fields (providers, facility, drug) when updating main service data
        const updated: any = {
            ...prevSvc,
            ...serviceData,
            providers: serviceData.providers !== undefined ? serviceData.providers : prevSvc.providers,
            facility: serviceData.facility !== undefined ? serviceData.facility : prevSvc.facility,
            drug: serviceData.drug !== undefined ? serviceData.drug : prevSvc.drug,
        };
        next[serviceIndex] = updated;
        this.services = next;
    };

    /**
     * Update only the facility of a service line.
     *
     * Use this from the Service Line Facility form so facility edits are isolated and not
     * affected by concurrent saves from the main service form.
     */
    @action
    public updateServiceLineFacility = (serviceIndex: number, facility: any | null): void => {
        const next = [...(this.services || [])];
        const svc = { ...(next[serviceIndex] || {}) };
        (svc as any).facility = facility || null;
        next[serviceIndex] = svc;
        this.services = next;
    };

    @action
    public deleteServiceLineFacility = (serviceIndex: number): void => {
        this.updateServiceLineFacility(serviceIndex, null);
    };

    @action
    public updateServiceLineProvider = (
        serviceIndex: number,
        type: 'RENDERING' | 'REFERRING' | 'SUPERVISING',
        provider: CreateClaimProvider | null,
    ): void => {
        const next = [...(this.services || [])];
        const svc: any = { ...(next[serviceIndex] || {}) };
        const providers: any[] = Array.isArray(svc.providers) ? [...svc.providers] : [];
        const normalizedType = String(type).toUpperCase();
        // Remove any existing provider of this type (normalize both sides)
        const filtered = providers.filter(p => p?.type !== type);
        // Optionally add/update this type
        if (provider) {
            filtered.push({ ...provider, type: normalizedType });
        }

        svc.providers = filtered;
        next[serviceIndex] = svc;
        this.services = next;
    };

    @action
    public deleteServiceLineProvider = (
        serviceIndex: number,
        type: 'RENDERING' | 'REFERRING' | 'SUPERVISING',
    ): void => {
        this.updateServiceLineProvider(serviceIndex, type, null);
    };

    /**
     * Update only the drug information of a service line.
     *
     * Use this from the Service Line Drug form so drug edits are isolated and not
     * affected by concurrent saves from the main service form.
     */
    @action
    public updateServiceLineDrug = (
        serviceIndex: number,
        drug: { x12NationalDrugCode?: string; x12DrugQuantity?: string; x12DrugMeasurement?: string } | null,
    ): void => {
        const next = [...(this.services || [])];
        const svc: any = { ...(next[serviceIndex] || {}) };
        (svc as any).drug = drug || null;
        next[serviceIndex] = svc;
        this.services = next;
    };

    @action
    public deleteServiceLineDrug = (serviceIndex: number): void => {
        this.updateServiceLineDrug(serviceIndex, null);
    };

    /**
     * Copy a service line and insert it right after the current one.
     * Generates a new service-line control number for the copied line.
     */
    @action
    public copyServiceLine = (serviceIndex: number): void => {
        const current = Array.isArray(this.services) ? this.services : [];
        if (serviceIndex < 0 || serviceIndex >= current.length) return;

        const sourceService = current[serviceIndex];
        if (!sourceService) return;

        // Deep copy the service line
        const copiedService: any = {
            ...sourceService
        };

        // Deep copy nested arrays/objects
        if (Array.isArray(sourceService.diagnosisCodes)) {
            copiedService.diagnosisCodes = [...sourceService.diagnosisCodes];
        }
        if (Array.isArray(sourceService.procedureModifierCodes)) {
            copiedService.procedureModifierCodes = [...sourceService.procedureModifierCodes];
        }
        if (Array.isArray(sourceService.providers)) {
            copiedService.providers = sourceService.providers.map((p: any) => ({ ...p }));
        }
        if (sourceService.facility) {
            const facilityCopy: any = { ...sourceService.facility };
            // Deep copy facility addresses if present
            if (Array.isArray(sourceService.facility.addresses)) {
                facilityCopy.addresses = sourceService.facility.addresses.map((addr: any) => ({ ...addr }));
            }
            copiedService.facility = facilityCopy;
        }

        // Insert the copied service right after the current one
        const next = [...current];
        next.splice(serviceIndex + 1, 0, copiedService);
        this.services = next;
    };

    @action
    public updateSubscribers = (subscribersData: CreateClaimSubscriber[] | null): void => {
        this.subscribers = subscribersData || [];
    };

    @action
    public copySubscriberFromPatient = (index: number): void => {
        const patient = this.patient;
        if (!patient || index < 0) return;

        const subscribers: CreateClaimSubscriber[] = Array.isArray(this.subscribers)
            ? [...this.subscribers]
            : [];

        const existing = subscribers[index] || ({} as CreateClaimSubscriber);

        subscribers[index] = {
            ...existing,
            first: patient.first,
            middle: patient.middle,
            last: patient.last,
            dob: patient.dob,
            sex: patient.sex as any,
            relationshipToPatient: RelationshipType.SELF as any,
            phone: patient.phones?.[0],
            addresses: patient.addresses,
        };

        this.subscribers = subscribers;
    };

    @action
    public updateBillingProviderId = (id: string | null): void => {
        this.billingProviderId = id;
    };

    @action
    public deleteBillingProvider = (): void => {
        this.billingProviderId = null;
    };

    @action
    public updateSettings = (settingsData: { maximumGuarantorResponsibility?: { amount: string; currency: string }; primaryThirdPartyAdministratorPayerId?: string; secondaryThirdPartyAdministratorPayerId?: string; tertiaryThirdPartyAdministratorPayerId?: string } | null): void => {
        this.settings = settingsData;
    };

    @action
    public updateguarantorPayments = (payments: Array<{ collectionDate?: string; amount?: { amount: string; currency: string }; memo?: string }> | null): void => {
        this.guarantorPayments = payments || [];
    };

    @action
    public updateSeller = (sellerData: { sellerId?: string; externalId?: string; first?: string; last?: string; organization?: string } | null): void => {
        this.seller = sellerData;
    };

    @action
    public updateNewDocument = (document: File | null): void => {
        this.newDocument = document;
    };

    @action
    public updateDocuments = (documentIds: string[]): void => {
        this.documents = documentIds;
    };

    public uploadDocument = async (file: File): Promise<string | null> => {
        try {
            const documentId = await this.documentsStore.uploadDocumentFile(file, {
                documentType: this.isSupportApp ? SupportDocumentType.MESSAGEDOCUMENT : ProviderCreateDocumentType.MESSAGEDOCUMENT,
            });

            // Update documents array
            runInAction(() => {
                this.documents = [...this.documents, documentId];
            });

            return documentId;
        } catch (error) {
            console.error('Failed to upload document:', error);
            return null;
        }
    };

    public fetchAllPayers = async (): Promise<ProviderPayer[]> => {
        try {
            const api = await CoreFetch<PayerApi>(PayerApi);
            const limit = 100;
            let allData: ProviderPayer[] = [];

            // Fetch first page to get total count
            const firstResponse = await (this.isSupportApp
                ? api.listSupportPayers({
                    limit,
                    offset: 0,
                    order: 'ASCENDING' as any,
                    sort: 'NAME' as any,
                } as any)
                : api.listProviderPayers({
                limit,
                offset: 0,
                order: 'ASCENDING' as any,
                sort: 'NAME' as any,
            } as any));

            // Note: The pagination-total header would need to be accessed from the response metadata
            // For now, we'll fetch pages until we get less than the limit
            allData = allData.concat(firstResponse || []);

            // Continue fetching if we got a full page
            let page = 1;
            let lastLength = (firstResponse || []).length;
            while (lastLength === limit) {
                const offset = page * limit;
                const response = await (this.isSupportApp
                    ? api.listSupportPayers({
                        limit,
                        offset,
                        order: 'ASCENDING' as any,
                        sort: 'NAME' as any,
                    } as any)
                    : api.listProviderPayers({
                    limit,
                    offset,
                    order: 'ASCENDING' as any,
                    sort: 'NAME' as any,
                } as any));
                
                if (!response || response.length === 0) break;
                allData = allData.concat(response);
                lastLength = response.length;
                page++;
                
                // Safety check to prevent infinite loop
                if (page > 100) break; // Max 10,000 records
            }

            // Deduplicate by id
            const deduplicatedData: ProviderPayer[] = [];
            const ids = new Set<string>();
            allData.forEach(item => {
                if (item.id && !ids.has(item.id)) {
                    ids.add(item.id);
                    deduplicatedData.push(item);
                }
            });

            return deduplicatedData;
        } catch (e) {
            console.warn('fetchAllPayers failed', e);
            return [];
        }
    };

    private isAddressNotEmpty = (addr: any): boolean => {
        if (!addr || typeof addr !== 'object') return false;
        return !!(
            (addr.name && String(addr.name).trim()) ||
            (Array.isArray(addr.lines) && addr.lines.some((line: any) => line && String(line).trim())) ||
            (addr.city && String(addr.city).trim()) ||
            (addr.state) ||
            (addr.postal && String(addr.postal).trim()) ||
            (addr.country)
        );
    };

    private addMailingTypeToAddresses = (addresses: any[] | undefined): any[] | undefined => {
        if (!Array.isArray(addresses) || addresses.length === 0) return addresses;
        return addresses.map((addr: any) => {
            if (this.isAddressNotEmpty(addr)) {
                return { ...addr, type: AddressType.MAILING };
            }
            return addr;
        });
    };

    private buildPayload = (): any => {
        const info = this.additionalInformation || ({} as any);
        const settings = this.settings || ({} as any);

        const services = (this.services || []).map((svc: any) => {
            const pos = svc.placeOfService;
            const mods: string[] = svc.procedureModifierCodes
                ? svc.procedureModifierCodes
                : [svc.procedureModifier1, svc.procedureModifier2, svc.procedureModifier3, svc.procedureModifier4].filter(Boolean);
            
            // Process facility addresses in service
            const facility = svc.facility ? {
                ...svc.facility,
                addresses: this.addMailingTypeToAddresses(svc.facility.addresses),
            } : svc.facility;

            // Drug information is already in TemplateDrugInformation format
            // Clean the drug object to remove empty fields but preserve the object if it has any valid fields
            let drug: any = undefined;
            if (svc.drug) {
                const cleanedDrug = this.deepClean(svc.drug);
                // Only include drug if it has at least one field after cleaning
                if (cleanedDrug && typeof cleanedDrug === 'object' && Object.keys(cleanedDrug).length > 0) {
                    drug = cleanedDrug;
                }
            }

            const servicePayload: any = {
                placeOfService: pos,
                fromDateOfService: svc.fromDateOfService,
                throughDateOfService: svc.throughDateOfService,
                diagnosisCodes: Array.isArray(svc.diagnosisCodes) ? svc.diagnosisCodes : [],
                procedureCode: svc.procedureCode,
                procedureModifierCodes: mods && mods.length ? mods : undefined,
                units: svc.units,
                emergency: svc.emergency,
                primaryPayerAuthorizationNumber: svc.primaryPriorAuthorizationNumber,
                secondaryPayerAuthorizationNumber: svc.secondaryPriorAuthorizationNumber,
                tertiaryPayerAuthorizationNumber: svc.tertiaryPriorAuthorizationNumber,
                narrative: svc.narrative,
                providers: svc.providers,
                facility,
            };
            
            // Explicitly add drug if it exists (don't let deepClean remove it if it's a valid object)
            if (drug !== undefined) {
                servicePayload.drug = drug;
            }

            return this.deepClean(servicePayload);
        });

        // Process addresses for patient, guarantor, subscribers, and facility
        const patient = this.patient ? {
            ...this.patient,
            addresses: this.addMailingTypeToAddresses(this.patient.addresses),
        } : this.patient;

        const guarantor = this.guarantor ? {
            ...this.guarantor,
            addresses: this.addMailingTypeToAddresses(this.guarantor.addresses),
        } : this.guarantor;

        const subscribers = Array.isArray(this.subscribers) && this.subscribers.length > 0
            ? this.subscribers.map((sub: any) => ({
                ...sub,
                addresses: this.addMailingTypeToAddresses(sub.addresses),
            }))
            : this.subscribers;

        const facility = this.facility ? {
            ...this.facility,
            addresses: this.addMailingTypeToAddresses(this.facility.addresses),
        } : this.facility;

        const raw = {
            // ProviderCreateClaimTypePayload
            controlNumber: info.controlNumber,
            billingProviderId: this.billingProviderId || undefined,
            maximumGuarantorResponsibility: settings.maximumGuarantorResponsibility || undefined,
            guarantorPayments: this.guarantorPayments,
            claimNarrative: info.claimNarrative,
            primaryPayerAuthorizationNumber: info.primaryPayerAuthorizationNumber,
            secondaryPayerAuthorizationNumber: info.secondaryPayerAuthorizationNumber,
            tertiaryPayerAuthorizationNumber: info.tertiaryPayerAuthorizationNumber,
            primaryThirdPartyAdministratorPayerId: settings.primaryThirdPartyAdministratorPayerId,
            secondaryThirdPartyAdministratorPayerId: settings.secondaryThirdPartyAdministratorPayerId,
            tertiaryThirdPartyAdministratorPayerId: settings.tertiaryThirdPartyAdministratorPayerId,
            subscribers,
            patient,
            guarantor,
            providers: this.providers,
            seller: this.seller || undefined,
            facility,
            documents: this.documents && this.documents.length > 0 ? this.documents : undefined,
            // ProviderCreateClaimProfessionalPayload
            admissionDate: info.admissionDate,
            dischargeDate: info.dischargeDate,
            otherDateType: (info.otherDateType as any) || undefined,
            fromOtherDate: info.fromOtherDate,
            throughOtherDate: info.throughOtherDate,
            services,
        };
        return this.deepClean(raw);
    };

    @action
    public submitClaim = async (): Promise<string | null> => {
        const payload = this.buildPayload();
        if (!this.canSubmit) {
            const blockers = this.getSubmitBlockers();
            const message = blockers && blockers.length ? `Cannot submit:\n- ${blockers.join('\n- ')}` : 'Please complete all required fields before submitting.';
            throw new Error(message);
        }

        if (this.config?.additionalValidation && !this.config.additionalValidation(payload)) {
            throw new Error('Additional validation failed');
        }

        try {
            this.isLoading = true;
            const api = await CoreFetch<ClaimApi>(ClaimApi);
            const result = this.isSupportApp
                ? await api.createSupportClaimProfessional({
                      supportCreateClaimProfessionalPayload: payload,
                  } as any)
                : await api.createProviderClaimProfessional({
                      providerCreateClaimProfessionalPayload: payload,
                  } as any);
            const id = (result as any)?.id || null;

            if (this.config?.onSubmit) {
                await this.config.onSubmit(payload);
            }

            return id;
        } catch (e) {
            console.error('submitClaim failed', e);
            return null;
        } finally {
            this.isLoading = false;
        }
    };

    @action
    public resetStore = (): void => {
        this.facility = null;
        this.patient = null;
        this.guarantor = null;
        this.providers = [];
        this.additionalInformation = null;
        this.services = [];
        this.subscribers = [];
        this.billingProviderId = null;
        this.isLoading = false;
        this.claimDetails = null;
        this.activePayload = null;
        this.settings = null;
        this.guarantorPayments = [];
        this.seller = null;
        this.newDocument = null;
        this.documents = [];
        this.isTemplate = false;
        this.isCreateClaim = true;
        this.originalControlNumber = null;
        this.initialClaimId = null;
        this.isControlNumberUnique = null;
        this.controlNumberCheckToken = 0;
        this.payersCache = [];
        this.payersLoaded = false;
    };

    public checkControlNumberUniqueness = async (controlNumber?: string | null): Promise<void> => {
        const value = (controlNumber || '').trim();
        const token = ++this.controlNumberCheckToken;

        if (!value) {
            runInAction(() => {
                if (token === this.controlNumberCheckToken) {
                    this.isControlNumberUnique = null;
                }
            });
            return;
        }

        if (this.originalControlNumber && value === this.originalControlNumber && !this.isTemplate) {
            runInAction(() => {
                if (token === this.controlNumberCheckToken) {
                    this.isControlNumberUnique = false;
                }
            });
            return;
        }

        runInAction(() => {
            if (token === this.controlNumberCheckToken) {
                this.isControlNumberUnique = null;
            }
        });

        const runLookup = async (params: any): Promise<boolean> => {
            const api = await CoreFetch<ClaimApi>(ClaimApi);
            const response = this.isSupportApp
                ? await api.listSupportClaims(params)
                : await api.listProviderClaims(params);
            return Array.isArray(response) && response.length > 0;
        };

        try {
            const paramsExternalId = { filterExternalId: [`EQ:${value}`], limit: 1, offset: 0 };
            let exists = await runLookup(paramsExternalId);

            if (!exists) {
                const paramsAccountNumber = { filterAccountNumber: [`EQ:${value}`], limit: 1, offset: 0 };
                exists = await runLookup(paramsAccountNumber);
            }

            runInAction(() => {
                if (token === this.controlNumberCheckToken) {
                    this.isControlNumberUnique = !exists;
                }
            });
        } catch (e) {
            console.warn('checkControlNumberUniqueness failed', e);
            runInAction(() => {
                if (token === this.controlNumberCheckToken) {
                    this.isControlNumberUnique = null;
                }
            });
        }
    };

    private updateIsCreateClaimStatus = (controlNumber?: string | null): void => {
        const hasClaimId = Boolean(this.initialClaimId);
        const baseCreate = !hasClaimId || this.isTemplate;
        const normalizedOriginal = (this.originalControlNumber || '').trim();
        const normalizedCurrent = (controlNumber || '').trim();
        const controlNumberChanged = hasClaimId && !this.isTemplate && normalizedCurrent !== normalizedOriginal;
        this.isCreateClaim = baseCreate || controlNumberChanged;
    };

    public fetchAllProviders = async (options?: { providerType?: string }): Promise<any[]> => {
        try {
            const api = await CoreFetch<ProviderApi>(ProviderApi);
            const limit = 100;
            const paramsBase: any = {
                limit,
                offset: 0,
            };
            if (options?.providerType) {
                paramsBase.filterProviderType = [`EQ:${options.providerType}`];
            }

            const listFn = this.isSupportApp ? api.listSupportProviders : api.listProviderProviders;

            let allData: any[] = [];
            let page = 0;
            while (true) {
                const params = { ...paramsBase, offset: page * limit };
                const response = await listFn(params);
                const items = response || [];
                if (items.length === 0) break;
                allData = allData.concat(items);
                if (items.length < limit) break;
                page++;
                if (page > 200) break; // safety: max 20,000 records
            }

            // Deduplicate by id
            const dedup: any[] = [];
            const seen = new Set<string>();
            for (const p of allData) {
                const id = (p as any)?.id;
                if (id && !seen.has(id)) {
                    seen.add(id);
                    dedup.push(p);
                }
            }
            return dedup;
        } catch (e) {
            console.warn('fetchAllProviders failed', e);
            return [];
        }
    };

    public preloadBillingProviders = async (): Promise<void> => {
        if (this.billingProvidersLoaded) return;
        try {
            const allBilling = await this.fetchAllBillingProviders();
            runInAction(() => {
                this.billingProvidersCache = allBilling;
                this.billingProvidersLoaded = true;
            });
        } catch (e) {
            console.warn('preloadBillingProviders failed', e);
        }
    };

    public preloadPayers = async (): Promise<void> => {
        if (this.payersLoaded) return;
        try {
            const allPayers = await this.fetchAllPayers();
            runInAction(() => {
                this.payersCache = allPayers;
                this.payersLoaded = true;
            });
        } catch (e) {
            console.warn('preloadPayers failed', e);
        }
    };

    public fetchAllBillingProviders = async (): Promise<any[]> => {
        try {
            const api = await CoreFetch<ProviderApi>(ProviderApi);
            const limit = 100;
            let allData: any[] = [];

            // First page
            const firstResponse = await (this.isSupportApp
                ? api.listSupportProviders({
                    limit,
                    offset: 0,
                    filterProviderType: ['EQ:BILLING'],
                } as any)
                : api.listProviderProviders({
                    limit,
                    offset: 0,
                    filterProviderType: ['EQ:BILLING'],
                } as any));

            allData = allData.concat(firstResponse || []);

            // Continue if full page
            let page = 1;
            let lastLength = (firstResponse || []).length;
            while (lastLength === limit) {
                const offset = page * limit;
                const response = await (this.isSupportApp
                    ? api.listSupportProviders({
                        limit,
                        offset,
                        filterProviderType: ['EQ:BILLING'],
                    } as any)
                    : api.listProviderProviders({
                        limit,
                        offset,
                        filterProviderType: ['EQ:BILLING'],
                    } as any));

                if (!response || response.length === 0) break;
                allData = allData.concat(response);
                lastLength = response.length;
                page++;
                if (page > 100) break; // safety
            }

            // Deduplicate by id
            const deduplicatedData: any[] = [];
            const ids = new Set<string>();
            allData.forEach((item: any) => {
                if (item.id && !ids.has(item.id)) {
                    ids.add(item.id);
                    deduplicatedData.push(item);
                }
            });

            return deduplicatedData;
        } catch (e) {
            console.warn('fetchAllBillingProviders failed', e);
            return [];
        }
    };

    public searchProvidersByLastName = async (
        lastNameQuery: string,
        providerType?: string,
    ): Promise<any[]> => {
        if (!lastNameQuery || lastNameQuery.trim().length === 0) {
            return [];
        }
        try {
            const api = await CoreFetch<ProviderApi>(ProviderApi);
            const params: any = {
                filterLast: [`LK:${lastNameQuery}%`],
                limit: 10,
            };
            if (providerType) {
                params.filterProviderType = [`EQ:${providerType}`];
            }
            const results = this.isSupportApp
                ? await api.listSupportProviders(params)
                : await api.listProviderProviders(params);
            return results || [];
        } catch (e) {
            console.warn('searchProvidersByLastName failed', e);
            return [];
        }
    };

    public getProviderById = async (providerId: string): Promise<any | null> => {
        if (!providerId) return null;
        try {
            const api = await CoreFetch<ProviderApi>(ProviderApi);
            return this.isSupportApp
                ? await api.getSupportProvider({ id: providerId } as any)
                : await api.getProviderProvider({ id: providerId } as any);
        } catch (e) {
            console.warn('getProviderById failed', e);
            return null;
        }
    };

    public searchPatientsByLastName = async (lastNameQuery: string): Promise<ProviderPatient[]> => {
        if (!lastNameQuery || lastNameQuery.trim().length === 0) {
            return [];
        }

        try {
            const api = await CoreFetch<PatientApi>(PatientApi);
            const params: any = { filterFirstLast: [`LK:${lastNameQuery}%`], limit: 10 };
            const results = this.isSupportApp
                ? await api.listSupportPatients(params)
                : await api.listProviderPatients(params);
            return results || [];
        } catch (e) {
            console.warn('searchPatientsByLastName failed', e);
            return [];
        }
    };

    public searchGuarantorsByLastName = async (lastNameQuery: string): Promise<ProviderGuarantor[]> => {
        if (!lastNameQuery || lastNameQuery.trim().length === 0) {
            return [];
        }

        try {
            const api = await CoreFetch<GuarantorApi>(GuarantorApi);
            const params: any = { filterLast: [`LK:${lastNameQuery}%`], limit: 10 };
            const results = this.isSupportApp
                ? await api.listSupportGuarantors(params)
                : await api.listProviderGuarantors(params);
            return results || [];
        } catch (e) {
            console.warn('searchGuarantorsByLastName failed', e);
            return [];
        }
    };

    public getPatientById = async (patientId: string): Promise<ProviderPatient | null> => {
        if (!patientId) return null;
        try {
            const api = await CoreFetch<PatientApi>(PatientApi);
            return this.isSupportApp
                ? await api.getSupportPatient({ id: patientId } as any)
                : await api.getProviderPatient({ id: patientId } as any);
        } catch (e) {
            console.warn('getPatientById failed', e);
            return null;
        }
    };

    public getGuarantorById = async (guarantorId: string): Promise<ProviderGuarantor | null> => {
        if (!guarantorId) return null;
        try {
            const api = await CoreFetch<GuarantorApi>(GuarantorApi);
            return this.isSupportApp
                ? await api.getSupportGuarantor({ id: guarantorId } as any)
                : await api.getProviderGuarantor({ id: guarantorId } as any);
        } catch (e) {
            console.warn('getGuarantorById failed', e);
            return null;
        }
    };

    public searchSubscribersByName = async (nameQuery: string): Promise<ProviderSubscriber[]> => {
        if (!nameQuery || nameQuery.trim().length === 0) {
            return [];
        }
        try {
            const api = await CoreFetch<SubscriberApi>(SubscriberApi);
            const params: any = { filterFirstLast: [`LK:${nameQuery}%`], limit: 10 };
            const results = this.isSupportApp
                ? await api.listSupportSubscribers(params)
                : await api.listProviderSubscribers(params);
            return results || [];
        } catch (e) {
            console.warn('searchSubscribersByName failed', e);
            return [];
        }
    };

    public getSubscriberById = async (subscriberId: string): Promise<ProviderSubscriber | null> => {
        if (!subscriberId) return null;
        try {
            const api = await CoreFetch<SubscriberApi>(SubscriberApi);
            return this.isSupportApp
                ? await api.getSupportSubscriber({ id: subscriberId } as any)
                : await api.getProviderSubscriber({ id: subscriberId } as any);
        } catch (e) {
            console.warn('getSubscriberById failed', e);
            return null;
        }
    };

    public searchPayersByName = async (nameQuery: string): Promise<ProviderPayer[]> => {
        if (!nameQuery || nameQuery.trim().length === 0) {
            return [];
        }
        try {
            const api = await CoreFetch<PayerApi>(PayerApi);
            const params: any = { filterName: [`LK:${nameQuery}%`], limit: 10 };
            const results = this.isSupportApp
                ? await api.listSupportPayers(params)
                : await api.listProviderPayers(params);
            return results || [];
        } catch (e) {
            console.warn('searchPayersByName failed', e);
            return [];
        }
    };

    public getPayerById = async (payerId: string): Promise<ProviderPayer | null> => {
        if (!payerId) return null;
        try {
            const api = await CoreFetch<PayerApi>(PayerApi);
            return this.isSupportApp
                ? await api.getSupportPayer({ id: payerId } as any)
                : await api.getProviderPayer({ id: payerId } as any);
        } catch (e) {
            console.warn('getPayerById failed', e);
            return null;
        }
    };

    public searchFacilitiesByName = async (nameQuery: string): Promise<any[]> => {
        if (!nameQuery || nameQuery.trim().length === 0) return [];
        try {
            const api = await CoreFetch<FacilityApi>(FacilityApi);
            const params: any = { filterName: [`LK:${nameQuery}%`], limit: 10 };
            const results = this.isSupportApp
                ? await api.listSupportFacilities(params)
                : await api.listProviderFacilities(params);
            return results || [];
        } catch (err) {
            console.warn('searchFacilitiesByName failed', err);
            return [];
        }
    };
}